import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { type PaginatedResponse } from "@/shared/http";
import type { SearchQueryBody } from "@/types";

import { type InvocationRecord } from "../ActionRecord";

export interface UseInvocationSearchOptions {
  /** The whole POST body: condition, sort, page size and continuation token. The hook adds nothing. */
  query: SearchQueryBody;
  refetchInterval?: number | false;
}

/**
 * One page of the current org's invocations. The body is in the query key by value, so a render
 * that rebuilds an identical body does not refetch; a changed filter, sort, page size or token does.
 */
export function useInvocationSearch({
  query,
  refetchInterval = false,
}: UseInvocationSearchOptions): UseQueryResult<
  PaginatedResponse<InvocationRecord>,
  Error
> {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery<PaginatedResponse<InvocationRecord>, Error>({
    queryKey: ["invocationSearch", currentOrganization?.org_id, query],
    queryFn: ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }
      return actionService.queryInvocations(query, {
        orgId: currentOrganization.org_id,
        abortSignal: signal,
      });
    },
    enabled: currentOrganization !== null,
    refetchInterval,
    staleTime: 15_000,
    // A page change or refresh keeps the previous rows on screen instead of blanking the grid.
    placeholderData: keepPreviousData,
  });
}
